import { AlertTriangle, LockOpen, LucideIcon } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { useGetPartialOperatorBalancesQuery } from "@/services/store-conciliation";
import { formatToBRL } from "@/utils/formatToMoney";
import { IPartialOperatorBalance } from "src/@types/store-conciliation";

import {
  PARTIAL_OPERATOR_COLUMNS,
  PARTIAL_OPERATOR_LABELS,
  PARTIAL_OPERATOR_ROW_HIGHLIGHT,
  PartialOperatorBalanceProps,
  PartialOperatorColumn,
  PartialOperatorRowMarker,
} from "./types";

type RowMarker = PartialOperatorRowMarker & { readonly Icon: LucideIcon };

const ROW_MARKERS: readonly RowMarker[] = [
  {
    key: "openCashRegister",
    applies: (operator: IPartialOperatorBalance) =>
      operator.is_open_cash_register,
    badge: PARTIAL_OPERATOR_LABELS.openCashRegisterBadge,
    hint: PARTIAL_OPERATOR_LABELS.openCashRegisterHint,
    legend: PARTIAL_OPERATOR_LABELS.openCashRegisterLegend,
    rowClassName: PARTIAL_OPERATOR_ROW_HIGHLIGHT.openCashRegister,
    iconClassName: "text-red-600",
    legendClassName: "text-red-800",
    Icon: LockOpen,
  },
  {
    key: "forcedClosing",
    applies: (operator: IPartialOperatorBalance) => operator.is_forced_closing,
    badge: PARTIAL_OPERATOR_LABELS.forcedClosingBadge,
    hint: PARTIAL_OPERATOR_LABELS.forcedClosingHint,
    legend: PARTIAL_OPERATOR_LABELS.forcedClosingLegend,
    rowClassName: PARTIAL_OPERATOR_ROW_HIGHLIGHT.forcedClosing,
    iconClassName: "text-amber-600",
    legendClassName: "text-amber-800",
    Icon: AlertTriangle,
  },
];

const renderCell = (
  operator: IPartialOperatorBalance,
  column: PartialOperatorColumn,
): string => {
  const value = operator[column.key];
  if (column.isMoney) return formatToBRL(Number(value));
  return String(value);
};

const markersOf = (operator: IPartialOperatorBalance): readonly RowMarker[] =>
  ROW_MARKERS.filter((marker) => marker.applies(operator));

const rowClassNameOf = (operator: IPartialOperatorBalance): string =>
  markersOf(operator)[0]?.rowClassName ?? "";

export default function PartialOperatorBalance({
  conciliation_id,
}: PartialOperatorBalanceProps) {
  const { data, isLoading, isError } = useGetPartialOperatorBalancesQuery(
    { conciliationId: conciliation_id ?? "" },
    { skip: !conciliation_id },
  );

  if (isLoading) {
    return (
      <p className="text-sm text-slate-500 py-6 px-2">
        {PARTIAL_OPERATOR_LABELS.loadingState}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-red-600 py-6 px-2">
        {PARTIAL_OPERATOR_LABELS.errorState}
      </p>
    );
  }

  const operators: IPartialOperatorBalance[] = data?.operators ?? [];

  if (operators.length === 0) {
    return (
      <p className="text-sm text-slate-500 py-6 px-2">
        {PARTIAL_OPERATOR_LABELS.emptyState}
      </p>
    );
  }

  const activeMarkers: readonly RowMarker[] = ROW_MARKERS.filter((marker) =>
    operators.some((operator) => marker.applies(operator)),
  );

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="w-full max-h-[70vh] overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 px-2" aria-label="Alerta" />
              {PARTIAL_OPERATOR_COLUMNS.map((column) => (
                <TableHead
                  key={column.key}
                  className={column.isMoney ? "text-right" : "text-left"}
                >
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {operators.map((operator) => (
              <TableRow
                key={`${operator.cash_register_code}-${operator.operator_code}-${operator.opening_time}`}
                className={rowClassNameOf(operator)}
              >
                <TableCell className="w-12 px-2 text-center whitespace-nowrap">
                  {markersOf(operator).map((marker) => (
                    <marker.Icon
                      key={marker.key}
                      className={`w-4 h-4 inline-block ${marker.iconClassName}`}
                      aria-label={marker.badge}
                    >
                      <title>{marker.hint}</title>
                    </marker.Icon>
                  ))}
                </TableCell>
                {PARTIAL_OPERATOR_COLUMNS.map((column) => (
                  <TableCell
                    key={column.key}
                    className={`${column.isMoney ? "text-right" : "text-left"} ${
                      column.key === "operator_name"
                        ? "whitespace-nowrap"
                        : ""
                    }`}
                  >
                    {renderCell(operator, column)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {activeMarkers.map((marker) => (
        <p
          key={marker.key}
          className={`flex items-center gap-2 text-xs px-2 ${marker.legendClassName}`}
        >
          <marker.Icon
            className={`w-3.5 h-3.5 shrink-0 ${marker.iconClassName}`}
          />
          {marker.legend}
        </p>
      ))}
    </div>
  );
}
